import { createBrowserRouter, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { isLowerEnv } from '@/config/utils';
import { AppSubscriber } from '@/containers/Subscriber/AppSubscriber';
import { RootVendors } from '@/containers/Subscriber/RootVendors';
import { Layout } from '@/Layout';
import { ProvideDefaultPaths } from '@/router/ProvideDefaultPaths';
import { Branding } from '@/screens/Branding';
import { Calendar } from '@/screens/Calendar';
import { ChatScreen } from '@/screens/Chat';
import { IFrameTest } from '@/screens/IFrameTest/IFrameTest';
import { OAuth2, OAuth2Logout } from '@/screens/Inbound';
import { OAuth2Login } from '@/screens/Inbound/Auth.OAuth2.Login';
import { LoginScreen } from '@/screens/Login';
import { LogoutScreen } from '@/screens/Logout';
import { ProjectMain } from '@/screens/Project.Main';
import { WorkspaceFile, FileNotFound } from '@/screens/Workspace.File';
import { WorkspaceFilesTable } from '@/screens/Workspace.FilesTable';
import { WorkspaceFolder } from '@/screens/Workspace.Folder';
import { Search as WorkspaceSearch } from '@/screens/WorkspaceGlobalSearch';
import { AppInitializedGuard } from './Guard.App';
import { AuthenticatedGuard, UnauthenticatedGuard } from './Guard.Authentication';
import { BrandingAccessGuard, BrandingInitializedGuard } from './Guard.Branding';
import { getAppDefaultPaths } from './utils';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

function buildRoutes() {
  const defaultPaths = getAppDefaultPaths();

  const mainRoute = {
    path: '/',
    element: (
      <RootVendors>
        <AppSubscriber>
          <ProvideDefaultPaths>
            <Layout />
          </ProvideDefaultPaths>
        </AppSubscriber>
      </RootVendors>
    ),
    errorElement: null, // note: trigger fallback component
    children: [
      { index: true, element: <Navigate to="/chat" /> },
      {
        path: '_/auth/oauth2/*',
        children: [
          { path: 'logout', Component: OAuth2Logout },
          { path: ':identifier', Component: OAuth2 },
        ],
      },
      { path: defaultPaths.login.substring(1), Component: OAuth2Login },
      {
        path: 'branding',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <BrandingAccessGuard>
                <Branding />
              </BrandingAccessGuard>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'calendar',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Calendar />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'chat/*',
        children: [
          {
            path: ':chatIdentifier?',
            element: (
              <AuthenticatedGuard>
                <AppInitializedGuard>
                  <ChatScreen />
                </AppInitializedGuard>
              </AuthenticatedGuard>
            ),
          },
        ],
      },
      {
        path: defaultPaths.manualLogin.substring(1),
        element: (
          <BrandingInitializedGuard>
            <UnauthenticatedGuard>
              <LoginScreen />
            </UnauthenticatedGuard>
          </BrandingInitializedGuard>
        ),
      },
      {
        path: 'logout',
        element: <LogoutScreen />,
      },
      {
        path: 'projects/:projectId/*',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <ProjectMain />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'workspaces',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <WorkspaceFilesTable />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'workspaces/search',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <WorkspaceSearch />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'workspaces/folders/:folderId/*',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <WorkspaceFolder />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'workspaces/files/not-found',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <FileNotFound />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'workspaces/files/:fileId/*',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <WorkspaceFile />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      // note: last catch all route
      { path: '*', element: <Navigate to={defaultPaths.home} replace /> },
    ],
  };

  const routes = [];

  if (isLowerEnv()) {
    routes.push({
      path: '/_iframe',
      element: <IFrameTest />,
    });
  }

  routes.push(mainRoute);
  return routes;
}

export function createRouter() {
  return sentryCreateBrowserRouter(buildRoutes());
}